import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type PartnerOfferActionKind } from '@/api/routes/partners/partners.offers.api';
import { IconMapPin } from '@/components/icon/tabler/IconMapPin';
import { IconPhoto } from '@/components/icon/tabler/IconPhoto';
import OfferCouponCode from '@/domain/offers/components/OfferCouponCode';
import { type OfferLocationFormValues } from '@/domain/offers/types';
import { formatOfferLocationAddress, getOfferActionLabel } from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';
import { controlTokens, surfaceTokens, radiusTokens } from '@/theme/themePrimitives';

interface Props {
  partnerName: string;
  title: string;
  body: string;
  imageUrl: string | null;
  couponCode: string | null;
  actions: { kind: PartnerOfferActionKind; label?: string | null }[];
  locations: (Pick<OfferLocationFormValues, 'street' | 'city' | 'state'> & {
    label?: string | null;
    number?: string | null;
    neighborhood?: string | null;
  })[];
}

/**
 * P18 preview: the app's `card` layout with what the form holds. The coupon is a dashed code, the
 * buttons follow their order (the first one filled) and the first place sums up "Onde vale".
 */
const OfferPreviewCard = ({
  partnerName,
  title,
  body,
  imageUrl,
  couponCode,
  actions,
  locations,
}: Props) => {
  const t = useTranslate('domain.offers.components.OfferPreviewCard');
  const [firstLocation] = locations;
  const locationText = firstLocation
    ? firstLocation.label?.trim() || formatOfferLocationAddress(firstLocation)
    : '';

  return (
    <Stack gap={1}>
      <Typography variant="caption" color="text.secondary">
        {t('caption')}
      </Typography>
      <Paper variant="outlined" sx={sx.card}>
        <Box sx={sx.image}>
          {imageUrl ? (
            <Box component="img" src={imageUrl} alt="" sx={sx.img} />
          ) : (
            <IconPhoto sx={{ fontSize: 32, color: 'text.disabled' }} />
          )}
        </Box>
        <Stack gap={0.75} sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={sx.partner}>
            {t('partner', { name: partnerName })}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={sx.clamp}>
            {title || t('titlePlaceholder')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={sx.clamp}>
            {body || t('bodyPlaceholder')}
          </Typography>
          {locationText ? (
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: 'text.secondary' }}>
              <IconMapPin sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={sx.ellipsis}>
                {locations.length > 1
                  ? t('locationMore', { place: locationText, count: locations.length - 1 })
                  : locationText}
              </Typography>
            </Stack>
          ) : null}
          {couponCode ? (
            <Box mt={0.5}>
              <OfferCouponCode code={couponCode} />
            </Box>
          ) : null}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mt={1}
          >
            <Typography variant="body2" color="text.secondary">
              {t('dismiss')}
            </Typography>
            {actions.map((action, index) => (
              <Box key={`${action.kind}-${index}`} sx={index === 0 ? sx.primary : sx.secondary}>
                {getOfferActionLabel(action)}
              </Box>
            ))}
            {!actions.length && !couponCode ? (
              <Box sx={[sx.primary, sx.placeholder]}>{t('ctaPlaceholder')}</Box>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default OfferPreviewCard;

const button = {
  px: 2,
  py: 0.75,
  borderRadius: radiusTokens.pill,
  typography: 'body2',
  fontWeight: 600,
  border: `1px solid ${controlTokens.coral}`,
};

const sx = {
  card: { borderRadius: radiusTokens.xxl, overflow: 'hidden', maxWidth: 360 },
  image: {
    aspectRatio: '16 / 9',
    bgcolor: 'action.hover',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  partner: { lineHeight: 1.4, letterSpacing: '0.06em' },
  clamp: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  primary: { ...button, bgcolor: controlTokens.coral, color: surfaceTokens.paper },
  secondary: { ...button, color: controlTokens.coral },
  placeholder: { opacity: 0.5 },
};
